import { supabase } from '@/utils/supabase/client';
import { Profile as ProfileType } from '@/types/supabase';

export async function fetchProfile(userId: string): Promise<ProfileType | null> {
  try {
    // Use Edge Function proxy to avoid client-side constructor issues
    const { data, error } = await supabase.functions.invoke('fetch-user-profile', {
      body: { user_id: userId },
    });

    if (error) {
      console.error("Proxy fetchProfile error:", error);
      throw error;
    }

    // Safe parsing: Ensure all fields are properly typed
    if (!data) {
      console.warn("No profile data returned from proxy");
      return null;
    }

    // Ensure dates are valid ISO strings (handle null/undefined safely)
    const createdAt = data.created_at ? new Date(data.created_at).toISOString() : new Date().toISOString();
    const updatedAt = data.updated_at ? new Date(data.updated_at).toISOString() : new Date().toISOString();

    const parsedProfile: ProfileType = {
      id: String(data.id || ''),
      email: String(data.email || ''),
      first_name: String(data.first_name || ''),
      last_name: String(data.last_name || ''),
      role: String(data.role || 'client') as 'user' | 'admin' | 'superadmin' | 'client',
      onboarding_completed: Boolean(data.onboarding_completed),
      mfa_enabled: Boolean(data.mfa_enabled),
      kyc_verified: Boolean(data.kyc_verified),
      company_id: data.company_id ? String(data.company_id) : null,
      created_at: createdAt,
      updated_at: updatedAt,
    };

    console.log('Fetched Profile (proxy):', parsedProfile);
    return parsedProfile;
  } catch (err) {
    console.error("Supabase fetchProfile error:", err);
    return null;
  }
}

// Export a function to fetch company via proxy (use similarly)
export async function fetchCompany(companyId: string) {
  try {
    const { data, error } = await supabase.functions.invoke('fetch-company-profile', {
      body: { company_id: companyId },
    });

    if (error) {
      console.error("Proxy fetchCompany error:", error);
      throw error;
    }

    if (!data) {
      console.warn("No company data returned from proxy");
      return null;
    }

    // Safe parsing for company
    const createdAt = data.created_at ? new Date(data.created_at).toISOString() : new Date().toISOString();

    const parsedCompany = {
      id: String(data.id || ''),
      name: String(data.name || 'Unknown Company'),
      subdomain: String(data.subdomain || ''),
      logo_url: data.logo_url ? String(data.logo_url) : null,
      primary_color: String(data.primary_color || '#3b82f6'),
      secondary_color: String(data.secondary_color || '#6b7280'),
      status: String(data.status || 'active'),
      created_at: createdAt,
    };

    console.log('Fetched Company (proxy):', parsedCompany);
    return parsedCompany;
  } catch (err) {
    console.error("Supabase fetchCompany error:", err);
    return null;
  }
}